import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import Button from '../common/Button';
import ToastContainer from '../common/ToastContainer';
import { useToast } from '../../hooks/useToast';
import { useAuthSync } from '../../hooks/useAuthSync';
import { useOrganizationTimezone } from '../../hooks/useOrganizationTimezone';
import CalendarBlockService from '../../services/calendarBlockService';
import UserService from '../../services/userService';
import { formatDateEs, formatDateTimeInTimezone } from '../../utils/dateUtils';
import './CalendarBlocks.css';

const WEEKDAY_LABELS = {
  MO: 'Lun',
  TU: 'Mar',
  WE: 'Mié',
  TH: 'Jue',
  FR: 'Vie',
  SA: 'Sáb',
  SU: 'Dom'
};

const getBlockTypeLabel = (type) => {
  if (type === 'BLOCK_RECURRING') return 'Recurrente';
  return 'Único';
};

const capitalizeFirstLetter = (value = '') => {
  if (!value) {
    return value;
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
};

const parseRRule = (rrule, timezone, fallbackTime = '') => {
  if (!rrule) return null;

  const lines = rrule.split('\n').filter(Boolean);
  const dtstartLine = lines.find((line) => line.startsWith('DTSTART:'));
  const ruleLine = lines.find((line) => line.startsWith('RRULE:')) || lines[0] || '';
  const rulePayload = ruleLine.replace('RRULE:', '');
  const parts = rulePayload.split(';').reduce((accumulator, part) => {
    const [key, value] = part.split('=');
    if (key && value) {
      accumulator[key] = value;
    }
    return accumulator;
  }, {});

  let startDate = '';
  if (dtstartLine) {
    const raw = dtstartLine.replace('DTSTART:', '');
    const parsed = DateTime.fromFormat(raw, "yyyyMMdd'T'HHmmss'Z'", { zone: 'utc' }).setZone(timezone);
    if (parsed.isValid) {
      startDate = parsed.toFormat('yyyy-MM-dd');
    }
  }

  let until = '';
  if (parts.UNTIL) {
    const parsedUntil = DateTime.fromFormat(parts.UNTIL, "yyyyMMdd'T'HHmmss'Z'", { zone: 'utc' }).setZone(timezone);
    if (parsedUntil.isValid) {
      until = parsedUntil.toFormat('yyyy-MM-dd');
    }
  }

  return {
    frequency: parts.FREQ || 'WEEKLY',
    interval: parts.INTERVAL || '1',
    byDay: parts.BYDAY ? parts.BYDAY.split(',') : [],
    until,
    startDate,
    startTime: fallbackTime
  };
};

const describeRecurringRule = (block, timezone) => {
  const parsed = parseRRule(block.rrule, timezone, block.startTime);
  if (!parsed) {
    return block.rrule;
  }

  const startCopy = parsed.startDate ? ` desde ${formatDateEs(parsed.startDate)}` : '';
  const untilCopy = parsed.until ? ` hasta ${formatDateEs(parsed.until)}` : '';
  const intervalValue = Number(parsed.interval || 1);
  const intervalCopy = intervalValue > 1 ? `cada ${intervalValue}` : 'cada';
  const timeCopy = block.allDay ? 'Todo el día' : `a las ${block.startTime}`;

  if (parsed.frequency === 'DAILY') {
    return `${intervalCopy} ${intervalValue > 1 ? 'días' : 'día'} ${timeCopy}${startCopy}${untilCopy}`;
  }

  if (parsed.frequency === 'MONTHLY') {
    return `${intervalCopy} ${intervalValue > 1 ? 'meses' : 'mes'} ${timeCopy}${startCopy}${untilCopy}`;
  }

  const byDayCopy = parsed.byDay.length > 0
    ? parsed.byDay.map((day) => WEEKDAY_LABELS[day] || day).join(', ')
    : 'los días seleccionados';

  return `${intervalCopy} ${intervalValue > 1 ? 'semanas' : 'semana'} (${byDayCopy}) ${timeCopy}${startCopy}${untilCopy}`;
};

const getSingleBlockEndDateTime = (block, timezone) => {
  const start = DateTime.fromISO(block.startDateTime, { setZone: true }).setZone(timezone);
  if (!start.isValid) {
    return null;
  }

  return start.plus({ minutes: Number(block.durationMinutes || 0) });
};

const getRecurringBlockUntilDateTime = (block, timezone) => {
  const parsed = parseRRule(block.rrule, timezone, block.startTime);
  if (!parsed?.until) {
    return null;
  }

  const until = DateTime.fromFormat(parsed.until, 'yyyy-MM-dd', { zone: timezone }).endOf('day');
  return until.isValid ? until : null;
};

const isPastBlock = (block, timezone) => {
  const now = DateTime.now().setZone(timezone);

  if (block.type === 'BLOCK_SINGLE') {
    const endDateTime = getSingleBlockEndDateTime(block, timezone);
    return endDateTime ? endDateTime < now : false;
  }

  if (block.type === 'BLOCK_RECURRING') {
    const untilDateTime = getRecurringBlockUntilDateTime(block, timezone);
    return untilDateTime ? untilDateTime < now : false;
  }

  return false;
};

const CalendarBlocks = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { timezone } = useOrganizationTimezone();
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();
  const { hasPermission, user: authUser } = useAuthSync();
  const canViewAll = hasPermission('agenda:viewall');
  const consumedNavigationMessageRef = useRef(null);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctorId, setSelectedDoctorId] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [activeTab, setActiveTab] = useState('current');
  const [isLoadingDoctors, setIsLoadingDoctors] = useState(true);
  const [isLoadingBlocks, setIsLoadingBlocks] = useState(false);

  const loadDoctors = useCallback(async () => {
    if (!auth.user?.access_token) return;

    setIsLoadingDoctors(true);
    try {
      if (!canViewAll && authUser) {
        const resolvedDoctorId = authUser.id || authUser.userId || auth.user?.profile?.sub || '';
        setDoctors([authUser]);
        setSelectedDoctorId(resolvedDoctorId);
        return;
      }

      const users = await UserService.getOrganizationUsers(auth.user.access_token);
      const resolvedDoctors = (users || []).filter((user) => Boolean(user.id || user.userId));
      const requestedDoctorId = new URLSearchParams(location.search).get('doctorId');
      setDoctors(resolvedDoctors);
      setSelectedDoctorId(requestedDoctorId || resolvedDoctors[0]?.id || resolvedDoctors[0]?.userId || '');
    } catch (error) {
      showError(error.message || 'No se pudieron cargar los doctores');
    } finally {
      setIsLoadingDoctors(false);
    }
  }, [auth.user?.access_token, auth.user?.profile?.sub, authUser, canViewAll, location.search, showError]);

  const loadBlocks = useCallback(async () => {
    if (!auth.user?.access_token || !selectedDoctorId) return;

    setIsLoadingBlocks(true);
    try {
      const data = await CalendarBlockService.getCalendarBlocks(selectedDoctorId, auth.user.access_token);
      setBlocks(data || []);
    } catch (error) {
      showError(error.message || 'No se pudieron cargar los bloqueos');
    } finally {
      setIsLoadingBlocks(false);
    }
  }, [auth.user?.access_token, selectedDoctorId, showError]);

  useEffect(() => {
    loadDoctors();
  }, [loadDoctors]);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  useEffect(() => {
    const navigationMessage = location.state?.message;

    if (!navigationMessage || consumedNavigationMessageRef.current === navigationMessage) {
      return;
    }

    consumedNavigationMessageRef.current = navigationMessage;
    showSuccess(navigationMessage);
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate, showSuccess]);

  const selectedDoctorName = useMemo(() => {
    const doctor = doctors.find((item) => (item.id || item.userId) === selectedDoctorId);
    return doctor?.fullName || doctor?.name || doctor?.username || doctor?.email || 'doctor';
  }, [doctors, selectedDoctorId]);

  const currentBlocks = useMemo(
    () => blocks.filter((block) => !isPastBlock(block, timezone)),
    [blocks, timezone]
  );

  const pastBlocks = useMemo(
    () => blocks.filter((block) => isPastBlock(block, timezone)),
    [blocks, timezone]
  );

  const visibleBlocks = activeTab === 'past' ? pastBlocks : currentBlocks;

  const renderSchedule = (block) => {
    if (block.type === 'BLOCK_SINGLE') {
      const { date, time } = formatDateTimeInTimezone(block.startDateTime, timezone);
      return block.allDay ? `${date} · Todo el día` : `${date} a las ${time}`;
    }

    return capitalizeFirstLetter(describeRecurringRule(block, timezone));
  };

  const renderDuration = (block) => (block.allDay ? 'Todo el día' : `${block.durationMinutes} min`);

  if (isLoadingDoctors) {
    return (
      <div className="calendar-blocks-container">
        <div className="calendar-blocks-panel calendar-blocks-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-blocks-container">
      <div className="calendar-blocks-header">
        <div className="header-content">
          <h1>Bloqueos de agenda</h1>
          <p>Reserva espacios no disponibles para un doctor, una vez o de forma recurrente.</p>
        </div>
        <div className="calendar-blocks-header-actions">
          <Button variant="secondary" onClick={() => navigate('/appointments')}>
            Volver a citas
          </Button>
          <Button
            variant="primary"
            onClick={() => navigate(`/appointments/blocks/${selectedDoctorId || auth.user?.profile?.sub || ''}/new`)}
            disabled={!selectedDoctorId}
          >
            Nuevo bloqueo
          </Button>
        </div>
      </div>

      <div className="calendar-blocks-panel">
        <div className="calendar-blocks-toolbar">
          <div className="calendar-blocks-select-group">
            <label htmlFor="calendar-blocks-doctor">Doctor</label>
            <select
              id="calendar-blocks-doctor"
              className="calendar-blocks-select"
              value={selectedDoctorId}
              onChange={(event) => setSelectedDoctorId(event.target.value)}
              disabled={!canViewAll}
            >
              {doctors.map((doctor) => {
                const doctorId = doctor.id || doctor.userId;
                const doctorName = doctor.fullName || doctor.name || doctor.username || doctor.email;
                return (
                  <option key={doctorId} value={doctorId}>
                    {doctorName}
                  </option>
                );
              })}
            </select>
          </div>

          <div className="calendar-blocks-toolbar-meta">
            {selectedDoctorName ? `Mostrando bloqueos de ${selectedDoctorName}` : 'Selecciona un doctor'}
          </div>
        </div>

        <div className="patients-tabs calendar-blocks-tabs">
          <button
            className={`tab ${activeTab === 'current' ? 'active' : ''}`}
            onClick={() => setActiveTab('current')}
          >
            <i className="fas fa-calendar-check"></i>
            Vigentes
            <span className="tab-count">{currentBlocks.length}</span>
          </button>
          <button
            className={`tab ${activeTab === 'past' ? 'active' : ''}`}
            onClick={() => setActiveTab('past')}
          >
            <i className="fas fa-clock"></i>
            Pasados
            {(pastBlocks.length > 0 || activeTab === 'past') && (
              <span className="tab-count">{pastBlocks.length}</span>
            )}
          </button>
        </div>

        {isLoadingBlocks ? (
          <div className="calendar-blocks-loading">
            <div className="loading-spinner"></div>
          </div>
        ) : blocks.length === 0 ? (
          <div className="calendar-blocks-empty">
            <h2>No hay bloqueos registrados</h2>
            <p>Usa esta sección para reservar tiempos no disponibles sin crear citas falsas.</p>
            <Button
              variant="primary"
              onClick={() => navigate(`/appointments/blocks/${selectedDoctorId || auth.user?.profile?.sub || ''}/new`)}
              disabled={!selectedDoctorId}
            >
              Crear primer bloqueo
            </Button>
          </div>
        ) : visibleBlocks.length === 0 ? (
          <div className="calendar-blocks-empty">
            <h2>{activeTab === 'current' ? 'No hay bloqueos vigentes' : 'No hay bloqueos pasados'}</h2>
            <p>
              {activeTab === 'current'
                ? 'Los bloqueos que sigan aplicando para este doctor aparecerán aquí.'
                : 'Aquí verás los bloqueos únicos ya terminados y las reglas recurrentes que ya vencieron.'}
            </p>
            {activeTab === 'current' && pastBlocks.length > 0 ? (
              <Button variant="secondary" onClick={() => setActiveTab('past')}>
                Ver bloqueos pasados
              </Button>
            ) : null}
            {activeTab === 'past' && currentBlocks.length > 0 ? (
              <Button variant="secondary" onClick={() => setActiveTab('current')}>
                Ver bloqueos vigentes
              </Button>
            ) : null}
          </div>
        ) : (
          <>
            <table className="calendar-blocks-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Horario</th>
                  <th>Duración</th>
                  <th>Motivo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visibleBlocks.map((block) => (
                    <tr key={block.id}>
                      <td>
                        <span className={`calendar-block-badge ${block.type === 'BLOCK_RECURRING' ? 'recurring' : 'single'}`}>
                          {getBlockTypeLabel(block.type)}
                        </span>
                      </td>
                      <td>
                        <div className="calendar-block-name">{renderSchedule(block)}</div>
                      </td>
                      <td>{renderDuration(block)}</td>
                      <td>{block.reason || 'Sin motivo'}</td>
                      <td>
                        <div className="calendar-block-actions">
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => navigate(`/appointments/blocks/${selectedDoctorId}/${block.id}/edit`)}
                          >
                            Editar
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            <div className="calendar-blocks-card-grid">
              {visibleBlocks.map((block) => (
                  <div key={block.id} className="calendar-block-card">
                    <div className="calendar-block-card-title">
                      <span className={`calendar-block-badge ${block.type === 'BLOCK_RECURRING' ? 'recurring' : 'single'}`}>
                        {getBlockTypeLabel(block.type)}
                      </span>
                    </div>
                    <p className="calendar-block-detail"><strong>Horario:</strong> {renderSchedule(block)}</p>
                    <p className="calendar-block-detail"><strong>Duración:</strong> {renderDuration(block)}</p>
                    <p className="calendar-block-detail"><strong>Motivo:</strong> {block.reason || 'Sin motivo'}</p>
                    <div className="calendar-block-actions">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => navigate(`/appointments/blocks/${selectedDoctorId}/${block.id}/edit`)}
                      >
                        Editar
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default CalendarBlocks;